import React, { useEffect, useMemo, useState } from 'react';
import {
  HistorianAnswerPresentation,
  HistorianChartPresentation,
  HistorianPlayerPresentation,
} from '../types/askArchive';
import './HistorianAnswerDisplay.css';

interface HistorianAnswerDisplayProps {
  answer: string;
  presentation?: HistorianAnswerPresentation;
}

const CHART_COLORS = ['#244b7c', '#c28a22', '#2f7b68', '#874f77'];

const formatNumber = (value: number) => new Intl.NumberFormat('en-CA', {
  maximumFractionDigits: 2,
}).format(value);

const PlayerPortrait: React.FC<{ player: HistorianPlayerPresentation }> = ({ player }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [player.imageUrl]);

  const initials = player.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  return (
    <div className="historian-player-portrait">
      {player.imageUrl && !failed ? (
        <img
          src={player.imageUrl}
          alt={`${player.name} headshot`}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-label={`${player.name} photo unavailable`}>{initials || player.position || '?'}</span>
      )}
    </div>
  );
};

const PlayerCard: React.FC<{
  player: HistorianPlayerPresentation;
  title: string | null;
}> = ({ player, title }) => {
  const facts = [
    ['Age', player.age === null ? 'Unknown' : formatNumber(player.age)],
    ['Position', player.position || 'Unknown'],
    ['NFL team', player.nflTeam || 'Not available'],
    ['League team', player.leagueTeam || 'Not on the latest roster'],
  ];

  return (
    <section className="historian-player-card" aria-label={`${player.name} player card`}>
      <PlayerPortrait player={player} />
      <div className="historian-player-card__body">
        {title && <span className="historian-presentation-title">{title}</span>}
        <h3>{player.name}</h3>
        <div className="historian-player-tags">
          {player.position && <span>{player.position}</span>}
          {player.nflTeam && <span>{player.nflTeam}</span>}
        </div>
        <dl>
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

const HistorianChart: React.FC<{
  chart: HistorianChartPresentation;
  title: string | null;
}> = ({ chart, title }) => {
  const width = 760;
  const height = 360;
  const margin = { top: 28, right: 24, bottom: 86, left: 68 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const labels = useMemo(() => {
    const seen = new Set<string>();
    return chart.series.flatMap((series) => series.data.map((point) => point.label))
      .filter((label) => {
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      });
  }, [chart.series]);
  const values = chart.series.flatMap((series) => series.data.map((point) => point.value));
  const minimum = Math.min(0, ...values);
  const maximum = Math.max(0, ...values);
  const range = maximum - minimum || 1;
  const scaleY = (value: number) => margin.top + ((maximum - value) / range) * innerHeight;
  const baselineY = scaleY(0);
  const ticks = Array.from({ length: 5 }, (_, index) => minimum + (range * index) / 4);
  const groupWidth = innerWidth / Math.max(labels.length, 1);
  const barWidth = Math.min(42, (groupWidth * 0.72) / Math.max(chart.series.length, 1));
  const xForLabel = (label: string) => margin.left + (labels.indexOf(label) + 0.5) * groupWidth;

  return (
    <section className="historian-chart" aria-label={title || 'Historian chart'}>
      {title && <h3>{title}</h3>}
      <div className="historian-chart__canvas">
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title || 'Chart of archive results'}>
          {ticks.map((tick) => {
            const y = scaleY(tick);
            return (
              <g key={tick}>
                <line className="historian-chart__grid" x1={margin.left} x2={width - margin.right} y1={y} y2={y} />
                <text className="historian-chart__tick" x={margin.left - 10} y={y + 4} textAnchor="end">
                  {formatNumber(tick)}
                </text>
              </g>
            );
          })}
          <line className="historian-chart__axis" x1={margin.left} x2={width - margin.right} y1={baselineY} y2={baselineY} />

          {chart.kind === 'bar' && chart.series.flatMap((series, seriesIndex) =>
            series.data.map((point) => {
              const center = xForLabel(point.label);
              const x = center - (barWidth * chart.series.length) / 2 + seriesIndex * barWidth;
              const y = point.value >= 0 ? scaleY(point.value) : baselineY;
              const barHeight = Math.abs(scaleY(point.value) - baselineY);
              return (
                <rect
                  key={`${series.name}-${point.label}`}
                  className="historian-chart__bar"
                  x={x}
                  y={y}
                  width={Math.max(barWidth - 2, 1)}
                  height={Math.max(barHeight, 1)}
                  rx="3"
                  fill={CHART_COLORS[seriesIndex % CHART_COLORS.length]}
                >
                  <title>{`${series.name}: ${point.label}, ${formatNumber(point.value)}`}</title>
                </rect>
              );
            }),
          )}

          {chart.kind === 'line' && chart.series.map((series, seriesIndex) => {
            const points = series.data.map((point) => `${xForLabel(point.label)},${scaleY(point.value)}`).join(' ');
            const color = CHART_COLORS[seriesIndex % CHART_COLORS.length];
            return (
              <g key={series.name}>
                <polyline className="historian-chart__line" points={points} fill="none" stroke={color} />
                {series.data.map((point) => (
                  <circle key={point.label} cx={xForLabel(point.label)} cy={scaleY(point.value)} r="4" fill={color}>
                    <title>{`${series.name}: ${point.label}, ${formatNumber(point.value)}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}

          {labels.map((label) => (
            <text
              key={label}
              className="historian-chart__label"
              x={xForLabel(label)}
              y={height - margin.bottom + 20}
              textAnchor="end"
              transform={`rotate(-35 ${xForLabel(label)} ${height - margin.bottom + 20})`}
            >
              {label.length > 18 ? `${label.slice(0, 17)}…` : label}
            </text>
          ))}
          {chart.yLabel && (
            <text className="historian-chart__axis-label" x={16} y={height / 2} textAnchor="middle" transform={`rotate(-90 16 ${height / 2})`}>
              {chart.yLabel}
            </text>
          )}
          {chart.xLabel && <text className="historian-chart__axis-label" x={margin.left + innerWidth / 2} y={height - 8} textAnchor="middle">{chart.xLabel}</text>}
        </svg>
      </div>
      {chart.series.length > 1 && (
        <div className="historian-chart__legend" aria-label="Chart legend">
          {chart.series.map((series, index) => (
            <span key={series.name}><i style={{ background: CHART_COLORS[index % CHART_COLORS.length] }} />{series.name}</span>
          ))}
        </div>
      )}
      <details className="historian-chart__data">
        <summary>View chart data</summary>
        <div className="historian-table-scroll">
          <table>
            <thead><tr><th>Series</th><th>Label</th><th>Value</th></tr></thead>
            <tbody>
              {chart.series.flatMap((series) => series.data.map((point) => (
                <tr key={`${series.name}-${point.label}`}><td>{series.name}</td><td>{point.label}</td><td>{formatNumber(point.value)}</td></tr>
              )))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
};

const HistorianAnswerDisplay: React.FC<HistorianAnswerDisplayProps> = ({ answer, presentation }) => {
  const resolved = presentation || { type: 'text', title: null };

  return (
    <div className={`historian-answer-content historian-answer-content--${resolved.type}`}>
      <p className="answer-summary">{answer}</p>
      {resolved.type === 'player' && <PlayerCard player={resolved.player} title={resolved.title} />}
      {resolved.type === 'list' && (
        <section className="historian-list">
          {resolved.title && <h3>{resolved.title}</h3>}
          <ol>
            {resolved.items.map((item, index) => (
              <li key={`${item.label}-${index}`}><strong>{item.label}</strong>{item.detail && <span>{item.detail}</span>}</li>
            ))}
          </ol>
        </section>
      )}
      {resolved.type === 'table' && (
        <section className="historian-table">
          {resolved.title && <h3>{resolved.title}</h3>}
          <div className="historian-table-scroll">
            <table>
              <thead><tr>{resolved.columns.map((column) => <th key={column} scope="col">{column}</th>)}</tr></thead>
              <tbody>{resolved.rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((value, columnIndex) => columnIndex === 0 ? <th key={columnIndex} scope="row">{value}</th> : <td key={columnIndex}>{value}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </section>
      )}
      {resolved.type === 'chart' && <HistorianChart chart={resolved.chart} title={resolved.title} />}
    </div>
  );
};

export default HistorianAnswerDisplay;
